import React, { useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { shouldShowMoreShow, ShowMore } from 'app/Components/common/ShowMore';
import {
  DrawerBodyStyled,
  DrawerCloseIcon,
  DrawerDataForm,
  DrawerHeaderRow,
  DrawerHeaderRowTitle
} from 'app/Components/common/Drawer/styles';
import { TError, TStandardSectionStep } from 'models';
import { TBroker } from 'app/Pages/common';
import { Input } from '@insly/qmt-reactjs-ui-lib';
import { processAPIError, filterUncommonErrors, translate } from 'utils';
import LoadingOverlay from 'helpers/LoadingOverlay';
import { renderErrors } from 'app/Components/common/Error';
import { renderControls, scrollElementToBottom, scrollElementToTop } from 'app/Components/common/Drawer';
import { ContactPersonRow, ContactsItem } from 'styles/common';
import ControlsRow from 'app/Components/common/DrawerHeaderControlsRow';

interface IProps {
  step: TStandardSectionStep,
  selectedUser: TBroker | null,
  brokerId?: string,
  handleDrawerClose: () => void,
  updateStep: (step: string) => void,
  handlePatchUser: (translation: TBroker, onError?: (error: AxiosError) => void, onSuccess?: () => void) => void,
  handleDeleteUser: () => void,
  updateSelectedUser: (user: TBroker) => void,
}

//@TODO: Update Drawer Lib component and make children not required and remove unnecessary <div />
const DrawerProfile = ({
  selectedUser,
  handleDrawerClose,
  updateStep,
  handlePatchUser,
  handleDeleteUser,
  step
}: IProps) => (selectedUser ? renderDrawerInner(selectedUser, handleDrawerClose, updateStep, handlePatchUser, handleDeleteUser, step) : <div />);

const renderDrawerInner = (
  selectedUser: TBroker,
  handleDrawerClose: () => void,
  updateStep: (step: string) => void,
  handlePatchUser: (selectedUser: TBroker, onError?: (errors: AxiosError) => void, onSuccess?: () => void) => void,
  handleDeleteBroker: (brokerId: string, onError: (error: AxiosError) => void) => void,
  step: TStandardSectionStep
) => (
  <>
    {HeaderRow(selectedUser, handleDrawerClose)}
    <ControlsRow
      step={step}
      updateStep={(step: string) => updateStep(`broker/${step}`)}
      onUserDelete={handleDeleteBroker}
      userId={selectedUser.id as string}
      defaultProfileStep="agents"
    />
    <DrawerBody
      step={step}
      brokerData={selectedUser}
      handlePatchUser={handlePatchUser}
    />
  </>
);

const DrawerBody = ({
  step,
  brokerData,
  handlePatchUser
}: {
  brokerData: TBroker,
  handlePatchUser: (selectedUser: TBroker, onError?: (errors: AxiosError) => void, onSuccess?: () => void) => void,
  step: TStandardSectionStep,
}) => {
  const bodyEl = useRef<HTMLDivElement>(null);
  const [showMore, updateShowMore] = useState(false);

  useEffect(() => {
    if (bodyEl.current !== null) {
      scrollElementToTop(bodyEl.current);
      shouldShowMoreShow(bodyEl.current, showMore, updateShowMore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerData.id]);

  const handleSaveUser = (brokerForm: TBroker, onError?: (error: AxiosError) => void, onSuccess?: () => void) => {
    const requestObject: TBroker = {
      ...brokerData,
      ...brokerForm
    };

    handlePatchUser(requestObject, onError, onSuccess);
  };

  const onBodyScroll = () => {
    if (bodyEl.current !== null) {
      shouldShowMoreShow(bodyEl.current, showMore, updateShowMore);
    }
  };

  return (
    <DrawerBodyStyled ref={bodyEl} onScroll={onBodyScroll} showMore={step === 'list'}>
      <UserForm
        brokerData={brokerData}
        handlePatchUser={handleSaveUser}
      />
      {step === 'list' ? <ShowMore show={showMore} onClick={() => scrollElementToBottom(bodyEl.current)} /> : null}
    </DrawerBodyStyled>
  );
};

const UserForm = ({
  brokerData,
  handlePatchUser,
}: {
  brokerData: TBroker,
  handlePatchUser: (userForm: TBroker, onError: (error: AxiosError) => void, onSuccess: () => void) => void,
}) => {

  const [form, updateForm] = useState<TBroker>({
    id: brokerData.id,
    name: brokerData.name,
    tag: brokerData.tag,
  });

  const [errors, updateErrors] = useState<TError[]>([]);
  const [isTouched, updateIsTouched] = useState(false);

  const [isSubmitting, updateIsSubmitting] = useState(false);

  useEffect(() => {
    updateForm({
      id: brokerData.id,
      name: brokerData.name,
      tag: brokerData.tag,
    });
    // eslint-disable-next-line
  }, [brokerData.id]);

  const handleChange = (key?: string, value?: string) => {
    const touchedForm = { ...form };
    // @ts-ignore
    touchedForm[key] = value;

    updateErrors([]);
    updateIsTouched(true);
    updateForm(touchedForm);
  };

  const handleSaveData = () => {
    updateIsSubmitting(true);

    handlePatchUser(form, (error) => {
      updateErrors(() => processAPIError(error, true) as TError[]);
      updateIsSubmitting(false);
    }, () => {
      updateIsTouched(false);
      updateIsSubmitting(false);
    });
    updateIsTouched(false);
  };

  return (
    <DrawerDataForm>
      {isSubmitting ? <LoadingOverlay /> : null}
      {renderUsersForm(form, handleChange)}
      {errors?.length ? renderErrors(filterUncommonErrors(errors)) : null}
      {(isTouched) ? renderControls('list', isTouched, handleSaveData,) : null}
    </DrawerDataForm>
  );

};

const renderUsersForm = (form: TBroker, handleChange: (key?: string, value?: string) => void) => (
  <ContactsItem>
    <ContactPersonRow gridTemplate="1fr">
      <Input
        label={translate({ key: 'master.common.name' })}
        name="name"
        value={form.name}
        handleChange={handleChange}
      />
    </ContactPersonRow>
    <ContactPersonRow gridTemplate="1fr">
      <Input
        label={translate({ key: 'master.common.tag' })}
        name="tag"
        value={form.tag}
        handleChange={handleChange}
      />
    </ContactPersonRow>
  </ContactsItem>
);

const HeaderRow = (brokerData: TBroker, handleDrawerClose: () => void) => (
  <DrawerHeaderRow>
    <DrawerHeaderRowTitle>{brokerData.name}</DrawerHeaderRowTitle>
    <DrawerCloseIcon icon="close" onClick={handleDrawerClose} />
  </DrawerHeaderRow>
);

export default DrawerProfile;
